import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  useTheme,
  alpha,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import s2image from "assets/s2image.jpg";

const SectionTwo = ({ aboutRef }) => {
  const theme = useTheme();
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.25 }
    );
    sectionRef.current && observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const fadeUp = (delay = 0) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(40px)",
    transition: `all 0.9s ease ${delay}ms`,
  });

  return (
    <Box
      ref={aboutRef}
      sx={{
        py: { xs: 10, md: 14 },
        background: "linear-gradient(180deg,#ffffff 0%,#f9fafb 100%)",
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 6, md: 10 }}
          alignItems="center"
          ref={sectionRef}
        >
          {/* IMAGE */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                ...fadeUp(0),
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 30px 60px rgba(0,0,0,0.12)",
              }}
            >
              <Box
                component="img"
                src={s2image}
                alt="AtHomeGeriCare"
                sx={{
                  width: "100%",
                  height: { xs: 360, md: 520 },
                  objectFit: "cover",
                  transition: "transform 1.2s ease",
                  "&:hover": {
                    transform: "scale(1.04)",
                  },
                }}
              />
            </Box>
          </Grid>

          {/* CONTENT */}
          <Grid item xs={12} md={6}>
            <Box>
              {/* Badge */}
              <Box
                sx={{
                  ...fadeUp(150),
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  mb: 3,
                  borderRadius: 50,
                  fontSize: 14,
                  fontWeight: 600,
                  background: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              >
                <StarIcon fontSize="small" />
                Our Story
              </Box>

              {/* Heading */}
              <Typography
                variant="h2"
                sx={{
                  ...fadeUp(250),
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: "2.4rem", md: "3.2rem" },
                  lineHeight: 1.2,
                }}
              >
                Care that feels like family
              </Typography>

              {/* Text */}
              <Typography
                sx={{
                  ...fadeUp(350),
                  color: "text.secondary",
                  fontSize: "1.05rem",
                  lineHeight: 1.9,
                  mb: 4,
                }}
              >
                AtHomeGeriCare was born from a deeply personal journey —
                understanding the emotional and practical challenges NRIs face
                while caring for elderly parents back home.
                <br />
                <br />
                We bridge that distance with compassion, trust, and professional
                care — so families can feel peace of mind, wherever they are.
              </Typography>

              {/* Divider */}
              <Box
                sx={{
                  ...fadeUp(450),
                  width: 80,
                  height: 3,
                  background: theme.palette.primary.main,
                  borderRadius: 2,
                  mb: 5,
                }}
              />

              {/* STATS */}
              <Grid container spacing={4}>
                {[
                  { value: "100+", label: "Families Supported" },
                  { value: "24/7", label: "Care Availability" },
                  { value: "30+", label: "Years Experience" },
                  { value: "98%", label: "Client Satisfaction" },
                ].map((item, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <Box
                      sx={{
                        ...fadeUp(550 + i * 120),
                        transition: "transform 0.3s ease, opacity 0.9s ease",
                        "&:hover": {
                          transform: "translateY(-6px)",
                        },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {item.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          letterSpacing: 0.5,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SectionTwo;

// import React from "react";
// import { Box, styled, Typography, Grid, Container, alpha } from "@mui/material";
// import s2image from "assets/s2image.jpg";
// import { motion } from "framer-motion";

// const AboutSection = styled(Box)(({ theme }) => ({
//   minHeight: "80vh",
//   display: "flex",
//   alignItems: "center",
//   padding: theme.spacing(10, 0),
//   backgroundColor: "#ffffff",
//   position: "relative",
//   overflow: "hidden",
//   "&::before": {
//     content: '""',
//     position: "absolute",
//     top: 0,
//     left: 0,
//     right: 0,
//     height: "100%",
//     background:
//       "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
//     zIndex: 0,
//   },
// }));

// const ImageContainer = styled(Box)(({ theme }) => ({
//   position: "relative",
//   height: "500px",
//   borderRadius: "25px",
//   overflow: "hidden",
//   boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
//   transformStyle: "preserve-3d",
//   "&::before": {
//     content: '""',
//     position: "absolute",
//     top: 0,
//     left: 0,
//     right: 0,
//     bottom: 0,
//     background:
//       "linear-gradient(45deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 100%)",
//     zIndex: 1,
//   },
//   "&::after": {
//     content: '""',
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     height: "150px",
//     background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
//     zIndex: 1,
//   },
//   [theme.breakpoints.down("md")]: {
//     height: "400px",
//     marginBottom: theme.spacing(4),
//   },
// }));

// const StyledImage = styled("img")({
//   width: "100%",
//   height: "100%",
//   objectFit: "cover",
//   transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
//   "&:hover": {
//     transform: "scale(1.1) rotate(-2deg)",
//   },
// });

// const ContentCard = styled(Box)(({ theme }) => ({
//   backgroundColor: "white",
//   borderRadius: "25px",
//   padding: theme.spacing(6),
//   boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
//   position: "relative",
//   zIndex: 1,
//   transform: "translateY(0)",
//   transition: "all 0.4s ease",
//   "&::before": {
//     content: '""',
//     position: "absolute",
//     top: -2,
//     left: -2,
//     right: -2,
//     bottom: -2,
//     background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//     borderRadius: "27px",
//     zIndex: -1,
//     opacity: 0,
//     transition: "opacity 0.4s ease",
//   },
//   "&:hover": {
//     transform: "translateY(-10px)",
//     boxShadow: "0 30px 80px rgba(0,0,0,0.15)",
//     "&::before": {
//       opacity: 0.1,
//     },
//   },
//   [theme.breakpoints.down("md")]: {
//     padding: theme.spacing(4),
//   },
// }));

// const SectionTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "3.5rem",
//   fontWeight: 800,
//   lineHeight: 1.1,
//   marginBottom: theme.spacing(4),
//   background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//   WebkitBackgroundClip: "text",
//   WebkitTextFillColor: "transparent",
//   fontFamily: "'Poppins', sans-serif",
//   position: "relative",
//   display: "inline-block",
//   "&::after": {
//     content: '""',
//     position: "absolute",
//     bottom: "-10px",
//     left: 0,
//     width: "80px",
//     height: "5px",
//     background: "linear-gradient(90deg, #667eea, #764ba2)",
//     borderRadius: "5px",
//   },
//   [theme.breakpoints.down("md")]: {
//     fontSize: "2.5rem",
//   },
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "2rem",
//   },
// }));

// const SectionText = styled(Typography)(({ theme }) => ({
//   fontSize: "1.1rem",
//   lineHeight: 1.8,
//   color: "#5d6d7e",
//   marginBottom: theme.spacing(3),
//   fontFamily: "'Inter', sans-serif",
//   fontWeight: 400,
// }));

// const StatsGrid = styled(Grid)(({ theme }) => ({
//   marginTop: theme.spacing(4),
// }));

// const StatItem = styled(Box)(({ theme }) => ({
//   textAlign: "center",
//   padding: theme.spacing(3),
//   borderRadius: "15px",
//   backgroundColor: "white",
//   boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
//   transition: "all 0.3s ease",
//   border: "1px solid rgba(0,0,0,0.05)",
//   "&:hover": {
//     transform: "translateY(-5px)",
//     boxShadow: "0 15px 40px rgba(102, 126, 234, 0.1)",
//     borderColor: alpha("#667eea", 0.3),
//   },
// }));

// const StatNumber = styled(Typography)(({ theme }) => ({
//   fontSize: "2.5rem",
//   fontWeight: 700,
//   background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//   WebkitBackgroundClip: "text",
//   WebkitTextFillColor: "transparent",
//   marginBottom: theme.spacing(1),
//   fontFamily: "'Poppins', sans-serif",
// }));

// const StatLabel = styled(Typography)(({ theme }) => ({
//   fontSize: "0.9rem",
//   color: "#7f8c8d",
//   fontWeight: 500,
//   textTransform: "uppercase",
//   letterSpacing: "1px",
// }));

// const SectionTwo = ({ aboutRef }) => {
//   return (
//     <AboutSection ref={aboutRef}>
//       <Container maxWidth="lg">
//         <Grid container spacing={6} alignItems="center">
//           {/* Left Column - Image */}
//           <Grid item xs={12} md={6}>
//             <motion.div
//               initial={{ opacity: 0, x: -50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               viewport={{ once: true, amount: 0.1 }}
//               transition={{ duration: 0.8, ease: "easeOut" }}
//             >
//               <ImageContainer>
//                 <StyledImage src={s2image} alt="About AtHomeGeriCare" />
//               </ImageContainer>
//             </motion.div>
//           </Grid>

//           {/* Right Column - Content */}
//           <Grid item xs={12} md={6}>
//             <motion.div
//               initial={{ opacity: 0, x: 50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               viewport={{ once: true, amount: 0.1 }}
//               transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
//             >
//               <ContentCard>
//                 <SectionTitle variant="h1">ABOUT US</SectionTitle>

//                 <SectionText>
//                   Welcome to AtHomeGeriCare, a service born from personal
//                   experience and a deep understanding of the challenges faced by
//                   NRIs in providing care for their elderly loved ones back in
//                   India.
//                 </SectionText>

//                 <SectionText>
//                   Our journey began with a simple mission: to provide
//                   compassionate, professional care that allows seniors to age
//                   gracefully in the comfort of their own homes.
//                 </SectionText>

//                 <StatsGrid container spacing={3}>
//                   <Grid item xs={6} sm={4}>
//                     <StatItem>
//                       <StatNumber>500+</StatNumber>
//                       <StatLabel>Families Served</StatLabel>
//                     </StatItem>
//                   </Grid>
//                   <Grid item xs={6} sm={4}>
//                     <StatItem>
//                       <StatNumber>50+</StatNumber>
//                       <StatLabel>Expert Caregivers</StatLabel>
//                     </StatItem>
//                   </Grid>
//                   <Grid item xs={6} sm={4}>
//                     <StatItem>
//                       <StatNumber>24/7</StatNumber>
//                       <StatLabel>Support</StatLabel>
//                     </StatItem>
//                   </Grid>
//                 </StatsGrid>
//               </ContentCard>
//             </motion.div>
//           </Grid>
//         </Grid>
//       </Container>
//     </AboutSection>
//   );
// };

// export default SectionTwo;
